import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import DateInput from "../DateInput";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import OperativePicker from "../hs/OperativePicker";

const FREQS = [["one_off", "One-off"], ["daily", "Daily"], ["weekly", "Weekly"], ["fortnightly", "Fortnightly"], ["monthly", "Monthly"], ["quarterly", "Quarterly"], ["six_monthly", "6-monthly"], ["annual", "Annual"]];

/** Create / edit a recurring inspection: template, location, frequency, who does it, first due date. */
export default function InspectionScheduleDialog({ open, onOpenChange, templates, schedule, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(schedule ? { ...schedule } : { template_id: templates?.[0]?.id || "", title: "", location: "", frequency: templates?.[0]?.frequency || "monthly", assigned: [], next_due: new Date().toISOString().slice(0, 10) });
  }, [open, schedule, templates]);

  const pickTemplate = (id) => { const t = templates.find(x => x.id === id); setForm(f => ({ ...f, template_id: id, frequency: t?.frequency || f.frequency })); };
  const save = async () => {
    setBusy(true);
    try {
      const body = { template_id: form.template_id, title: form.title, location: form.location, frequency: form.frequency, assigned: form.assigned, next_due: form.next_due };
      const { data } = schedule ? await API.put(`/hse/inspections/schedules/${schedule.id}`, body) : await API.post("/hse/inspections/schedules", body);
      toast.success(schedule ? "Schedule updated" : "Inspection scheduled");
      onSaved?.(data); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="insp-schedule-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">{schedule ? "Edit schedule" : "Schedule an inspection"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Checklist</Label>
            <select value={form.template_id || ""} onChange={e => pickTemplate(e.target.value)} disabled={!!schedule} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="insp-schedule-template">
              {(templates || []).map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            {!templates?.length && <p className="text-[11px] text-amber-700">No active checklists yet — build and activate one under Templates first.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Title (optional)</Label><Input value={form.title || ""} onChange={e => setForm({ ...form, title: e.target.value })} className="h-9" placeholder="Defaults to checklist name" data-testid="insp-schedule-title" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Location / site</Label><Input value={form.location || ""} onChange={e => setForm({ ...form, location: e.target.value })} className="h-9" placeholder="e.g. Head office, Van LS12 ABC" data-testid="insp-schedule-location" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Frequency</Label>
              <select value={form.frequency || "monthly"} onChange={e => setForm({ ...form, frequency: e.target.value })} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="insp-schedule-frequency">
                {FREQS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></div>
            <div className="space-y-1.5"><Label className="text-xs">{schedule ? "Next due" : "First due"}</Label><DateInput value={form.next_due || ""} onChange={e => setForm({ ...form, next_due: e.target.value })} className="w-44" data-testid="insp-schedule-due" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Who completes it</Label><OperativePicker value={form.assigned || []} onChange={v => setForm({ ...form, assigned: v })} testPrefix="insp-schedule-assigned" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form.template_id} data-testid="insp-schedule-save">{busy ? "Saving…" : schedule ? "Save" : "Schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
